import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Paper,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

function PolicyModelTester({ model, onClose }) {
  const [fields, setFields] = useState([]);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadingFields, setLoadingFields] = useState(true);

  useEffect(() => {
    loadModelFields();
  }, [model]);

  const loadModelFields = async () => {
    if (!model?.id) return;

    setLoadingFields(true);
    try {
      const response = await fetch(`/api/v1/policy/models/${model.id}/fields`);
      if (response.ok) {
        const data = await response.json();
        setFields(data.fields || []);
        
        // Initialize inputs with empty values
        const initialInputs = {};
        (data.fields || []).forEach((field) => {
          initialInputs[field.name] = '';
        });
        setInputs(initialInputs);
      }
    } catch (err) {
      console.error('Failed to load fields:', err);
    } finally {
      setLoadingFields(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setInputs((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Filter out empty inputs
      const filteredInputs = {};
      Object.keys(inputs).forEach((key) => {
        if (inputs[key] !== '' && inputs[key] !== null && inputs[key] !== undefined) {
          filteredInputs[key] = inputs[key];
        }
      });

      const response = await fetch(`/api/v1/policy/models/${model.id}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inputs: filteredInputs }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.result);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to query model');
      }
    } catch (err) {
      setError(`Error querying model: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getFieldType = (field) => {
    const type = field.type?.toLowerCase() || 'string';
    if (type === 'enum' && field.options) {
      return 'select';
    }
    if (type === 'number' || type === 'integer' || type === 'float') {
      return 'number';
    }
    if (type === 'boolean' || type === 'bool') {
      return 'boolean';
    }
    return 'text';
  };

  const renderFieldInput = (field) => {
    const fieldType = getFieldType(field);
    const value = inputs[field.name] || '';

    switch (fieldType) {
      case 'select':
        return (
          <TextField
            fullWidth
            select
            label={field.name}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            SelectProps={{
              native: true,
            }}
            helperText={field.description}
            required={field.required}
          >
            <option value="">Select...</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </TextField>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.name}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            helperText={field.description}
            required={field.required}
          />
        );

      case 'boolean':
        return (
          <TextField
            fullWidth
            select
            label={field.name}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            SelectProps={{
              native: true,
            }}
            helperText={field.description}
            required={field.required}
          >
            <option value="">Select...</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </TextField>
        );

      default:
        return (
          <TextField
            fullWidth
            label={field.name}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            helperText={field.description}
            required={field.required}
            multiline={field.type === 'text' && field.description?.length > 50}
            rows={field.type === 'text' && field.description?.length > 50 ? 3 : 1}
          />
        );
    }
  };

  return (
    <Box>
      {loadingFields ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Input Fields
                  </Typography>

                  {fields.length === 0 ? (
                    <Alert severity="info">
                      No fields defined in this model. You can still test with custom inputs.
                    </Alert>
                  ) : (
                    <Stack spacing={3}>
                      {fields.map((field) => (
                        <Box key={field.name}>
                          {renderFieldInput(field)}
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                      onClick={handleTest}
                      disabled={loading}
                      sx={{ borderRadius: 2 }}
                    >
                      {loading ? 'Testing...' : 'Test Model'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Results
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  {!result && !error && (
                    <Alert severity="info">
                      Enter values and click "Test Model" to see results
                    </Alert>
                  )}

                  {result && (
                    <Stack spacing={2}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: result.permit_type !== 'unknown' ? 'success.light' : 'warning.light',
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {result.permit_type !== 'unknown' ? (
                            <SuccessIcon sx={{ mr: 1, color: 'success.main' }} />
                          ) : (
                            <ErrorIcon sx={{ mr: 1, color: 'warning.main' }} />
                          )}
                          <Typography variant="h6">
                            Permit Type: {result.permit_type || 'Unknown'}
                          </Typography>
                        </Box>
                        {result.message && (
                          <Typography variant="body2" color="text.secondary">
                            {result.message}
                          </Typography>
                        )}
                      </Paper>

                      {result.missing_fields && result.missing_fields.length > 0 && (
                        <Alert severity="warning">
                          <Typography variant="subtitle2" gutterBottom>
                            Missing Required Fields:
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                            {result.missing_fields.map((field) => (
                              <Chip key={field} label={field} size="small" />
                            ))}
                          </Stack>
                        </Alert>
                      )}

                      {result.requirements_met && result.requirements_met.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Requirements Met:
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                            {result.requirements_met.map((field) => (
                              <Chip
                                key={field}
                                label={field}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2">Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={2}>
                            {result.confidence !== undefined && (
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Confidence: {(result.confidence * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            )}

                            {result.path_taken && result.path_taken.length > 0 && (
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Decision Path:
                                </Typography>
                                <Stack spacing={0.5}>
                                  {result.path_taken.map((step, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="caption"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {idx + 1}. {step}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {result.metadata && (
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Metadata:
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {JSON.stringify(result.metadata, null, 2)}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

export default PolicyModelTester;

